import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import { Header } from '@/components/modules/Header';
import { useIsUserCountryForDubaiMid } from '@/hooks';
import { CONSTANTS } from '@/constants';

const TermsOfUse: React.FC = () => {
	const router = useRouter();
	const isDubaiMid = useIsUserCountryForDubaiMid();

	return (
		<section className='static-page'>
			<Header
				showMenuButton
				showCenterLogo
				action={{
					icon: 'back',
					goBackCallback: () => router.back(),
				}}
			/>
			<div className='container old-styles'>
				<h1>TERMS AND CONDITIONS OF USE</h1>
				<p>
					<b>📍 IMPORTANT NOTICE REGARDING AUTOMATIC RENEWALS</b>
				</p>
				<p>
					<b>This Service includes subscriptions that automatically renew.</b>{' '}
					Please read these terms and conditions of use (the “<b>Terms</b>”)
					carefully (in particular, Section V. SUBSCRIPTION FEES AND PAYMENT)
					before starting a trial or completing a purchase for our auto-renewing
					subscription service.
				</p>
				<p>
					To <b>avoid being charged</b>, you must{' '}
					<b>
						cancel your subscription before the end of your trial or current
						billing cycle.
					</b>{' '}
					By purchasing an automatically renewing subscription, you acknowledge
					and agree to its recurring nature, as explained near the point of
					purchase. If you do not cancel in time, your subscription will
					automatically renew, and the applicable charges will be applied.
				</p>
				<p>
					If you subscribed or started a trial directly via our <b>website</b>,
					please contact our support team at{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>{' '}
					for assistance.
				</p>
				<p>
					<b>
						Deleting your account does not cancel your subscription or trial.
					</b>{' '}
					If you intend to cancel, ensure you follow the appropriate
					cancellation process. You may also wish to take a screenshot of this
					notice for future reference. More details can be found in our{' '}
					<Link href='/subscription-terms' prefetch={false}>
						<b>Subscription Terms</b>
					</Link>
					.
				</p>
				<p>
					Our privacy practices are described in detail in our{' '}
					<Link href='/privacy-policy' prefetch={false}>
						<b>Privacy Policy</b>
					</Link>
					. Please review it to understand how we collect, use, and share your
					personal information.
				</p>
				<p>
					<b>📍 BINDING ARBITRATION &amp; DISPUTE RESOLUTION</b>
				</p>
				<p>
					Section XI of these Terms governs how disputes between you and Lift
					are resolved.{' '}
					<b>
						In particular, it includes a binding arbitration agreement, which
						means:
					</b>
				</p>
				<ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
					<li>
						<p>
							You agree to resolve disputes with us through{' '}
							<b>final and binding arbitration</b>, rather than in court, except
							for certain limited exceptions.
						</p>
					</li>
					<li>
						<p>
							You <b>waive your right</b> to file a lawsuit or participate in a
							class action lawsuit against us.
						</p>
					</li>
					<li>
						<p>
							You may opt out of the arbitration agreement by following the
							process outlined in Section XI.
						</p>
					</li>
				</ul>

				<p>
					Please read this section carefully, as it significantly affects your
					legal rights.
				</p>

				<p>
					<b>ACCEPTANCE OF TERMS</b>
				</p>
				<p>
					{isDubaiMid ? (
						`1.1. These Terms and Conditions (the “Terms”) govern the relationship between you and FT SICH FZCO,
						a legal entity incorporated under the laws of the United Arab Emirates,
						having its registered office at IFZA Business Park, DDP, Building A2, 21379-001, Dubai,
						United Arab Emirates ("we" "us" "our" or the "Company") regarding your use of the Company’s websites, including
						liftstory.com, liftapp.ai and lift.bio, (the “Websites”), mobile app
						“Lift: Story Maker” (the “App”) and other services (together the
						“Service”), including all textual, graphic, video, music, software and
						other content available through the Service (together the “Content”). `
					) : (
						<>
							The provisions of the “Terms” govern the relationship between you
							and FunplaceApp Limited, a company registered under the laws of
							Cyprus, with registered office at 8 Ioustinianou Street, Flat 302,
							2413 Nicosia, Cyprus (“<b>we</b>”, “<b>us</b>”, “<b>our</b>” or
							the “<b>Company</b>”) regarding your use of the Company’s
							websites, mobile apps, devices and related services (the “
							<b>Web</b>” or “<b>Service</b>”), including all information, text,
							graphics, software, and services, available for your use (the “
							<b>Content</b>”).
						</>
					)}
				</p>
				<p>
					By <b>accessing or using any part of the Service</b>, you acknowledge
					that you have read, understood, and agree to be bound by these Terms,
					forming a legally binding agreement between you and the Company.{' '}
					<b>
						If you do not agree to these Terms, you must immediately stop using
						the Service, delete your account, and cancel any active
						subscriptions.
					</b>
				</p>
				<p>
					These Terms were originally drafted in English. If there is any
					conflict between the English language version of these Terms and a
					version translated into another language, the English-language version
					will prevail.
				</p>
				<p>
					<b>Additional Terms and Policies</b>
				</p>
				<p>
					Our{' '}
					<Link href='/privacy-policy' prefetch={false}>
						<b>Privacy Policy</b>
					</Link>{' '}
					forms an integral part of these Terms and describes how we collect,
					use, and protect your personal data. We may also post additional
					policies, supplemental terms, or notices on the Service from time to
					time.{' '}
					<b>
						Such terms are hereby incorporated by reference and will apply to
						your use of the Service.
					</b>
				</p>
				<p>
					<b>Changes to these Terms</b>
				</p>
				<p>
					We{' '}
					<b>
						may update, modify, or remove portions of these Terms at our sole
						discretion
					</b>
					, to the extent permitted by applicable law. This may occur when we
					introduce or discontinue features, technologies, or services, to
					comply with legal, regulatory, or contractual requirements, or in
					response to exceptional or unforeseen circumstances. Where required by
					law, we will notify you of such changes.
				</p>
				<p>
					Unless stated otherwise, we will indicate updates by revising the
					"Last Updated" date of these Terms.{' '}
					<b>
						You acknowledge and agree that it is your responsibility to review
						the Terms regularly for any updates. Unless specified otherwise, the
						updated Terms take effect once posted on the Service.
					</b>{' '}
					By continuing to use the Service after the updates become effective,
					you agree to the revised Terms. If you do not agree, you must stop
					using the Service immediately, delete your account and cancel your
					subscription.
				</p>
				<p>
					<b>Changes to the Service</b>
				</p>
				<p>
					We may also update, change, suspend or discontinue the Service (or any
					part, content or feature) at any time, without notice and without
					liability to you or anyone else (for example, to offer or test new or
					different features, technologies, or services, to repair, improve or
					further develop the Service, to comply with legal, regulatory or
					contractual requirements, or in response to exceptional or unforeseen
					circumstances). Some services and features may not be available in all
					countries, in all languages, or in all operating systems.
				</p>

				<p>
					<b>ACCOUNT REGISTRATION</b>
				</p>
				<p>
					<b>Creating an Account</b>
				</p>
				<p>
					To access certain features of the Service, you may be required to{' '}
					<b>register an account</b> ("<b>Account</b>") and provide accurate and
					complete information during the registration process.
				</p>
				<p>
					<b>Your Responsibilities</b>
				</p>
				<p>
					By creating an Account, you represent and warrant that: (1) the
					information you provide is <b>truthful, accurate, and up to date;</b>{' '}
					(2) You will <b>update your Account information as needed</b> to keep
					it accurate; (3) Your use of the Service complies with{' '}
					<b>all applicable laws, regulations, and these Terms</b>.
				</p>
				<p>
					Failure to provide or maintain accurate information may impact the
					functionality of the Service, and we may be unable to notify you of
					important updates.
				</p>
				<p>
					<b>Age Restriction</b>
				</p>
				<p>
					The Service is not intended to be used by individuals under the age of
					16. You hereby represent and warrant to the Company that you meet the
					foregoing qualification. All users who are minors in the jurisdiction
					in which they reside must have the permission of, and be directly
					supervised by, their parent or guardian to use the Service. If you are
					a minor, you must have your parent or guardian read and agree to these
					Terms prior to you using the Service.
				</p>
				<p>
					<b>Account Suspension or Termination</b>
				</p>
				<p>
					We reserve the right to <b>suspend or terminate your Account</b> and
					restrict your access to the Service{' '}
					<b>at our discretion, with or without prior notice</b>, if we
					determine that you have violated these Terms or any applicable laws.
					This includes cases where you have provided false, misleading, or
					incomplete information during registration or engaged in fraudulent,
					abusive, or unauthorized activity on the Service. Termination may
					result in the loss of access to your data, content, or any benefits
					associated with the Service, and we are not responsible for any
					consequences resulting from such actions.
				</p>
				<p>
					<b>Account Security</b>
				</p>
				<p>
					You are responsible for maintaining the confidentiality of your
					Account credentials, including login details and passwords, and for
					all activity conducted under your Account. You should not share your
					login information with anyone, as you assume full responsibility for
					any actions taken through your Account. If you suspect unauthorized
					access or a security breach, you must notify us immediately at{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>
					.
				</p>
				<p>
					We are not liable for any loss, unauthorized transactions, or damage
					resulting from access to your Account due to your failure to protect
					your credentials. It is your responsibility to use secure passwords
					and take necessary precautions to prevent unauthorized access.
				</p>

				<p>
					<b>USE OF THE SERVICE</b>
				</p>
				<p>
					<b>Ownership and Intellectual Property</b>
				</p>
				<p>
					The Service, including its software, content, logos, trademarks, and
					any associated materials, remains the exclusive property of the
					Company or its licensors. Accessing or using the Service does not
					grant you ownership of any intellectual property rights beyond what is
					explicitly stated in these Terms. You may not copy, modify,
					distribute, sell, or reverse-engineer any portion of the Service
					unless expressly permitted.
				</p>
				<p>
					<b>License to Use the Service</b>
				</p>
				<p>
					You are granted a limited, non-exclusive, non-transferable, revocable
					license to access and use the Service for personal, non-commercial
					purposes. This license allows you to install and use the App on your
					personal mobile device but does not permit sublicensing, resale,
					modification, or unauthorized use. Any breach of these Terms may
					result in the immediate suspension or termination of your access to
					the Service.
				</p>
				<p>
					<b>User-Generated Content</b>
				</p>
				<p>
					The Service may allow you to submit, upload, or share text, images,
					messages, feedback, and other materials ("<b>User Content</b>"). By
					submitting User Content, you grant the Company, its sublicensees,
					successors, and assigns a royalty-free, perpetual, irrevocable,
					sublicensable, assignable, worldwide license to use, reproduce,
					modify, adapt, translate, publish, distribute, publicly display, and
					create derivative works from your content in any form, media, or
					technology, whether now known or later developed. This license
					includes the right to incorporate User Content into other works and
					services, including marketing, analytics, and operational
					improvements. However, this license explicitly excludes any personal
					data as defined under applicable privacy laws, which will be handled
					in accordance with the Company’s Privacy Policy.
				</p>
				<p>
					<b>User Reviews and Testimonials</b>
				</p>
				<p>
					By submitting, posting, or otherwise providing any review, rating,
					comment, testimonial, or other feedback (“Review”) about the Service
					on any platform, including but not limited to the Apple App Store,
					Google Play Store, and other mobile application marketplaces,
					websites, social media platforms, or directly to the Company, you
					grant the Company and its affiliates a non-exclusive, worldwide,
					perpetual, irrevocable, royalty-free, sublicensable, and transferable
					right to use, reproduce, modify, adapt, publish, translate,
					distribute, publicly perform, publicly display, and create derivative
					works from such Reviews for any lawful purpose, including but not
					limited to marketing, advertising, promotional activities, product
					development, and customer engagement, in any media now known or later
					developed, without further notice, attribution, or compensation to
					you.
				</p>
				<p>You acknowledge and agree that:</p>
				<ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
					<li>
						<p>
							The Company is not obligated to use, display, or maintain any
							Review and may remove or edit Reviews at its discretion.
						</p>
					</li>
					<li>
						<p>
							The Company may use Reviews in conjunction with your publicly
							displayed username, profile picture, or other identifying
							information (if available), unless you request anonymity in
							writing.
						</p>
					</li>
					<li>
						<p>
							The Company is not responsible for the content of Reviews posted
							by users and does not endorse any opinions expressed therein.
						</p>
					</li>
				</ul>
				<p>
					If you wish to request the removal of a Review that you have
					submitted, you may contact the Company at{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>
					.
				</p>
				<p>
					<b>User Responsibilities and Rights</b>
				</p>
				<p>
					You retain ownership of any User Content you submit, subject to the
					license granted to the Company. By submitting User Content, you
					represent and warrant that you own or have all necessary rights and
					permissions to grant the above license. You confirm that your content
					does not infringe upon any third-party intellectual property, privacy,
					or contractual rights and that any third-party rights, including moral
					rights, in the User Content have been lawfully waived or granted to
					you. The Company and its successors may use the User Content without
					compensating you.
				</p>
				<p>
					Once submitted, User Content may remain accessible even if removed by
					you. If you wish to revoke the granted license for specific User
					Content, you may contact{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>
					. However, any prior use of the content will not be affected. The
					Company is not responsible for storing or maintaining copies of
					removed User Content and is not liable for any loss incurred due to
					its removal.
				</p>
				<p>
					<b>Content Moderation and Restrictions</b>
				</p>
				<p>
					The Company does not actively monitor User Content but reserves the
					right to review, edit, remove, or restrict content at its discretion.
					This applies particularly to content that contains offensive, illegal,
					defamatory, or misleading material, violates third-party intellectual
					property, privacy, or contractual rights, promotes harm, violence,
					harassment, or illegal activity, or disrupts the functionality,
					security, or reputation of the Service. The Company assumes no
					liability for the accuracy or legality of User Content submitted by
					others.
				</p>
				<p>
					<b>Prohibited Conduct</b>
				</p>
				<p>
					You agree not to use the Service to distribute illegal, deceptive, or
					harmful content, impersonate another individual or misrepresent your
					affiliation, reverse-engineer, extract, or manipulate any part of the
					Service, or interfere with the security, availability, or integrity of
					the Service. Violation of these Terms may result in the immediate
					suspension or termination of your account, as well as legal
					consequences.
				</p>
				<p>
					<b>Service Availability and Modifications</b>
				</p>
				<p>
					The Company reserves the right to modify, suspend, or discontinue any
					aspect of the Service at any time without liability. Certain features
					may not be available in all regions or on all devices. If a
					modification impacts your use of the Service, you may cancel your
					subscription or delete your account.
				</p>
				<p>
					Also please make sure you are using the latest version of your web
					browser when accessing the Website, as that will help prevent security
					problems and ensure all the Website features work for you.
				</p>
				<p>
					<b>Risks and Disclaimer of Liability</b>
				</p>
				<p>
					Your use of the Service is at your own risk. The Company does not
					guarantee the accuracy, reliability, or fitness of any content
					provided. We are not responsible for loss of data, device
					malfunctions, or technical failures, any reliance on fitness,
					nutrition, or wellness recommendations within the Service, or personal
					injury, financial loss, or any legal claims arising from your use of
					the Service.
				</p>
				<p>
					<b>Customer Support</b>
				</p>
				<p>
					Customer support services are provided at the Company’s discretion.
					While we may assist users, there is no obligation to provide support
					or respond to inquiries. If you require assistance, contact{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>
					, and we will respond as reasonably possible.
				</p>

				<p>
					<b>THIRD-PARTY SERVICES, MATERIALS, AND ADVERTISING</b>
				</p>
				<p>
					The Service may integrate, provide access to, or display content from
					third-party services, websites, software, advertisements, and other
					materials ("<b>Third-Party Services</b>" and "
					<b>Third-Party Materials</b>"). This includes external links, embedded
					content, and user-generated materials contributed by third parties.
					While these features may be accessible through the Service, the
					Company does not control or assume responsibility for the content,
					functionality, or policies of any Third-Party Services.
				</p>
				<p>
					<b>No Endorsement or Responsibility</b>
				</p>
				<p>
					By using the Service, you acknowledge that the Company does not
					endorse, verify, or assume responsibility for the accuracy, legality,
					quality, or reliability of any Third-Party Services or Third-Party
					Materials. Some of this content may be objectionable, offensive, or
					misleading, and the Company is not liable for any exposure to such
					material. Any interactions, transactions, or agreements you engage in
					with third parties through the Service are solely between you and the
					respective third party. The Company bears no responsibility for any
					disputes, losses, or issues that may arise from these interactions.
				</p>
				<p>
					<b>Third-Party Links and Advertising</b>
				</p>
				<p>
					The Service may include advertisements, sponsored content, or links to
					third-party websites that are not owned or controlled by the Company.
					Clicking on third-party links or engaging with external services does
					not establish any endorsement, affiliation, or sponsorship between the
					Company and the third party. Any engagement with such content is at
					your own risk. It is your responsibility to review and comply with the
					terms, policies, and privacy practices of third-party services before
					using them. The Company disclaims any liability for how third parties
					collect, process, or use your data.
				</p>
				<p>
					<b>No Liability for Third-Party Content</b>
				</p>
				<p>
					The Company does not monitor, evaluate, or guarantee the accuracy,
					completeness, or legality of Third-Party Materials. To the fullest
					extent permitted by law, the Company makes no express or implied
					warranties regarding third-party content and disclaims all liability
					for any loss, damage, or harm resulting from your reliance on or use
					of such content. Some third-party materials may be outdated,
					misleading, or otherwise unreliable, and you assume full
					responsibility for any decisions based on this content.
				</p>
				<p>
					<b>Use of Third-Party Services at Your Own Risk</b>
				</p>
				<p>
					Accessing Third-Party Services through the Service is entirely
					voluntary. You assume all risks associated with interacting with
					third-party content, including potential malware, phishing scams, or
					deceptive practices. The Company is not responsible for any technical
					issues, disputes, or damages arising from your engagement with
					Third-Party Services. By using such services, you waive any claims
					against the Company related to your interactions with third-party
					content, advertisements, or external links.
				</p>
				<p>
					If you encounter harmful, misleading, or offensive third-party content
					while using the Service, you may report it to the Company. However,
					the Company is not obligated to investigate, remove, or take action
					against third-party content unless required by law.
				</p>

				<p>
					<b>SUBSCRIPTION FEES AND PAYMENT</b>
				</p>
				<p>
					<b>Subscription Options and Purchases</b>
				</p>
				<p>
					The Service offers subscription-based access to its features and
					content which may be purchased either directly from the Company
					through the Website ("<b>Web Purchase</b>") or via an App Store ("
					<b>In-App Purchase</b>"). All applicable subscription fees, billing
					terms, and durations (e.g., weekly, monthly, quarterly, annually) will
					be displayed on the payment screen or at checkout before payment
					authorization. Our pricing varies based on a number of factors
					including, but not limited to, region, bundle, and duration of
					subscription. Some limited features of the Service may be available
					free of charge, but full access requires a paid subscription.
				</p>
				<p>
					<b>Purchases and Payment Processing</b>
				</p>
				<p>
					Upon completing the onboarding process on the Website or in the App,
					you will be presented with available subscription options, their
					pricing, durations, and accepted payment methods (e.g., Mastercard,
					Visa, PayPal, Apple Pay, Google Pay). By selecting a subscription and
					authorizing the payment, you instruct the applicable payment processor
					or App Store to charge your selected payment method. Once the payment
					is validated, you will receive access to the Service.
				</p>
				<p>
					For Web Purchases, payments are handled by third-party payment
					processors, which you authorize to charge your selected payment
					method. These processors handle transaction processing and notify us
					of successful payments. For In-App Purchases, payments are processed
					by the App Store, and the respective App Store’s terms and policies
					govern the transaction.
				</p>
				<p>
					<b>Auto-Renewal and Subscription Continuity</b>
				</p>
				<p>
					All subscriptions automatically renew unless canceled. The renewal
					period matches the initial subscription term unless otherwise
					disclosed at the time of purchase. To avoid renewal, you must cancel
					your subscription before the renewal date.
				</p>
				<p>
					By proceeding with a subscription, you acknowledge that charges will
					be applied periodically based on the selected billing cycle. The
					renewal rate will be no more than the rate for the immediately prior
					subscription period, excluding any promotional (introductory) and
					discount pricing, unless we notify you of a rate change prior to your
					auto-renewal.
				</p>
				<p>
					For Web Purchases, cancellation must be completed through the
					Website’s account settings or by following the cancellation
					instructions provided at the time of purchase. For In-App Purchases,
					cancellation must be done through the respective App Store’s account
					settings. <b>Deleting the App does not cancel your subscription.</b>
				</p>
				<p>
					<b>Add-On Items and Additional Services</b>
				</p>
				<p>
					In addition to your subscription, you may have the option to purchase
					add-on items such as premium content, consultations, or supplementary
					features. These add-ons may be one-time purchases or recurring
					charges. Canceling your main subscription will also cancel any
					associated recurring add-ons, but canceling an add-on alone does not
					affect your primary subscription.
				</p>
				<p>
					<b>Refunds</b>
				</p>
				<p>
					If you subscribed or started a free trial via the <b>App Store</b>,
					refund requests are handled directly by <b>Apple</b>. You can submit a
					request following the instructions on the Apple Support page.
				</p>
				<p>
					If you subscribed or started a free trial through the{' '}
					<b>Google Play Store</b> or directly via our <b>website</b>, please
					contact our support team at{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>{' '}
					for assistance.
				</p>
				<p>
					<b>
						When you make the Web Purchase, you acknowledge and agree that all
						Web Purchases are non-refundable or exchangeable
					</b>
					. Notwithstanding anything to the contrary in the foregoing, the
					Company will provide refunds and/or Purchase cancellations in cases
					and to the extent required by mandatory provisions of the applicable
					law. The Company may also provide refunds at its own discretion and
					subject to our policies that may be published from time to time.
				</p>
				<p>App Store refunds are subject to their policies.</p>
				<p>
					<b>Right of Withdrawal for the EU and UK Residents</b>
				</p>
				<p>
					If you are a resident of the European Union, you have the legal right
					to withdraw from a contract for the purchase of digital services
					within <b>14 days of your purchase</b>, without providing any reason
					and without incurring any additional costs. If you have received
					access to a free trial, the withdrawal period will expire{' '}
					<b>14 days from the start of your free trial</b>, rather than from the
					date of payment.
				</p>
				<p>
					To exercise your right of withdrawal, you must notify us by email at{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>{' '}
					stating your decision to withdraw from the contract. While you may use
					the model withdrawal form provided below, it is not mandatory. Your
					withdrawal request will be considered valid if sent before the 14-day
					withdrawal period expires.
				</p>
				<p>
					If you exercise your right of withdrawal, we will refund all payments
					received from you without undue delay, and in any event{' '}
					<b>no later than 14 days</b> from the date we receive your withdrawal
					notice. Refunds will be processed using the same payment method used
					for the original transaction, unless you have expressly agreed
					otherwise. You will not incur any fees as a result of the
					reimbursement.
				</p>
				<p>
					If you have <b>expressly consented</b> to the immediate supply of the
					Service before the expiration of the withdrawal period and
					acknowledged that you will lose your right to withdraw, you will not
					be eligible for a refund for any digital content that has already been
					delivered. In the case of digital services, you may be eligible for a{' '}
					<b>proportional refund</b>, based on the portion of the Service
					provided before your withdrawal request. If this provision applies, we
					will provide you with confirmation of your prior express consent and
					acknowledgment on a durable medium.
				</p>
				<p>
					<b>Model Withdrawal Form</b>
				</p>
				<p>
					<b>To:</b> Lift, email:{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>
					<br />
					<b>Subject:</b> Exercise of Right of Withdrawal
				</p>

				<p>
					I hereby notify you of my withdrawal from the contract for the
					purchase of the following service:
				</p>
				<p>
					<b>Service Name:</b>
					<br />
					<b>Date of Purchase / Free Trial Start:</b>
					<br />
					<b>Full Name:</b>
					<br />
					<b>Email Address:</b>
					<br />
					<b>Payment Method Used:</b>
					<br />
					<b>Date of Request:</b>
				</p>

				<p>(Signature required if submitted by mail)</p>
				<p>
					<b>Note for U.S. Residents</b>
				</p>
				<p>
					If you reside in <b>California</b>, you have the right to cancel your
					purchase at any time <b>before midnight of the third business day</b>{' '}
					following the date of the purchase. If you exercise this right, we
					will issue a full refund of the amount paid.
				</p>
				<p>
					To request a refund under this policy, you must notify us of your
					cancellation by contacting{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>{' '}
					before the cancellation deadline. Refunds will be processed using the
					same payment method used for the original transaction, and no
					additional fees will be incurred for the reimbursement.
				</p>
				<p>
					<b>Chargebacks and Payment Disputes</b>
				</p>
				<p>
					If you wish to request a refund for a payment made using a
					reimbursable method, such as a credit or debit card, we strongly
					encourage you to <b>contact us first</b> at{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>{' '}
					before initiating a chargeback with your payment provider. This allows
					us the opportunity to review your request and attempt to resolve the
					issue directly.
				</p>
				<p>
					Refunds, when applicable, are <b>not processed in real-time</b>. If we
					confirm that a refund has been issued, please allow{' '}
					<b>at least 15 business days</b> for the refund to reflect in your
					account. You acknowledge that you are{' '}
					<b>
						not entitled to receive multiple refunds for the same transaction
					</b>{' '}
					and agree that if you request a refund directly from us, you will not
					initiate a separate refund request or chargeback through your payment
					provider <b>unless your request has been denied by us</b>. If you
					receive duplicate refunds due to separate refund requests, we reserve
					the right to work with your payment provider to reverse one of those
					refunds.
				</p>
				<p>
					Initiating a chargeback or reversing a payment through your bank or
					payment provider may result in the{' '}
					<b>immediate termination of your account</b> at our sole discretion,
					as it indicates that you have determined you no longer wish to use our
					Service. If a chargeback is overturned in our favor, you may contact{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>{' '}
					to discuss reinstating your account.
				</p>
				<p>
					As outlined in our Privacy Policy, your{' '}
					<b>
						personally identifiable information may be shared with our payment
						processor
					</b>{' '}
					to assist in responding to chargeback requests.
				</p>
				<p>
					If you initiate a chargeback or payment dispute, we may suspend or
					terminate your access to the Service. Fraudulent or improper
					chargebacks may result in a permanent ban from using the Service and
					potential legal action.
				</p>
				<p>
					<b>Trials and Promotional Offers</b>
				</p>
				<p>
					We may offer trials providing temporary access to the Service. The
					trial duration and terms will be displayed at sign-up. If you do not
					cancel before the trial ends, your subscription will automatically
					convert into a paid subscription, and the applicable fee will be
					charged.
				</p>
				<p>
					We do not guarantee reminders before the trial expires. It is your
					responsibility to track the trial period and cancel if you do not wish
					to continue. The Company reserves the right to modify, revoke, or
					restrict trial eligibility at any time.
				</p>
				<p>
					<b>Promotional Codes</b>
				</p>
				<p>
					We may provide you with gift cards or promotional codes that can be
					redeemed for additional features, enhancements, functionalities,
					content, services within a specified Service and for a limited period
					of time, subject eligibility requirements (the “
					<b>Promotional Codes</b>”). Promotional Codes have no cash value, are
					personal, non-transferable, non-sublicensable, and we are under no
					obligation to provide any compensation in connection with a
					Promotional Code.
				</p>
				<p>
					<b>Changes to Subscription Fees</b>
				</p>
				<p>
					To the maximum extent permitted by applicable law, we may modify
					subscription fees at any time. If notification is required under
					applicable law, we will inform you of pricing changes in the manner
					and timeframe mandated by regulations. Where no specific timeframe is
					prescribed, we will provide notice by posting the updated prices in
					the App, sending an email notification, or using other prominent
					communication methods. The revised pricing will take effect as
					specified in the notice.
				</p>
				<p>
					If you do not agree to the updated fees, you may cancel your
					subscription before the new pricing takes effect or refrain from
					prepaying for continued access to the Service.
				</p>
				<p>
					<b>Failure to Pay and Service Termination</b>
				</p>
				<p>
					If a payment is declined or not received when due, we may notify you
					to update your payment method. However, if the issue is not resolved,
					we reserve the right to{' '}
					<b>
						suspend or terminate your access to the Service without further
						notice
					</b>
					. Any content, data, or personalized settings associated with your
					account may be lost, and we are not responsible for restoring them.
				</p>

				<p>
					<b>USER REPRESENTATION AND RESTRICTIONS</b>
				</p>
				<p>By accessing or using the Service, you confirm that:</p>
				<ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
					<li>
						<p>
							You have the legal capacity to enter into and comply with these
							Terms.
						</p>
					</li>
					<li>
						<p>
							You are <b>at least 16 years old</b> and legally permitted to use
							the Service.
						</p>
					</li>
					<li>
						<p>
							You will not access the Service through{' '}
							<b>automated or non-human means</b>, including bots, scripts, or
							similar methods.
						</p>
					</li>
					<li>
						<p>
							You will not use the Service for any{' '}
							<b>unlawful, fraudulent, or unauthorized purpose</b>.
						</p>
					</li>
					<li>
						<p>
							You are not located in a country subject to{' '}
							<b>U.S. government embargo restrictions</b> or designated as a{' '}
							<b>terrorist-supporting nation</b>.
						</p>
					</li>
					<li>
						<p>
							You are not listed on any{' '}
							<b>U.S. government list of prohibited or restricted persons</b>.
						</p>
					</li>
					<li>
						<p>
							Your use of the Service complies with all{' '}
							<b>applicable laws and regulations</b>.
						</p>
					</li>
				</ul>
				<p>
					If any information you provide is false, misleading, outdated, or
					incomplete, we reserve the right to{' '}
					<b>deny or terminate your current or future access</b> to the Service.
				</p>
				<p>
					<b>Permitted Use of the Service</b>
				</p>
				<p>
					The Service is made available for its <b>intended purposes only</b>.
					You may not use the Service for any unauthorized, commercial, or
					competitive activities unless expressly approved by us.
				</p>
				<p>
					<b>Prohibited Conduct</b>
				</p>
				<p>
					You agree <b>not to engage</b> in the following activities when using
					the Service:
				</p>
				<ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
					<li>
						<p>
							Collecting, scraping, or systematically retrieving data or other
							content from the Service to create a{' '}
							<b>database, compilation, or directory</b> without our express
							permission.
						</p>
					</li>
					<li>
						<p>
							Using the Service for any <b>unauthorized purposes</b>, including
							modifying, adapting, improving, or creating derivative works from
							the Service.
						</p>
					</li>
					<li>
						<p>
							Using the Service for{' '}
							<b>commercial or revenue-generating endeavors</b>, unless
							explicitly approved by us.
						</p>
					</li>
					<li>
						<p>
							Making the Service accessible{' '}
							<b>
								over a network that allows multiple devices or users to access
								it simultaneously
							</b>
							, unless permitted.
						</p>
					</li>
					<li>
						<p>
							Developing, launching, or using the Service to{' '}
							<b>create a competing product, service, or software</b>.
						</p>
					</li>
					<li>
						<p>
							Using any of our proprietary information, intellectual property,
							or interfaces to develop, license, or distribute applications,
							accessories, or other related products.
						</p>
					</li>
					<li>
						<p>
							Circumventing, disabling, or interfering with{' '}
							<b>security features</b> of the Service.
						</p>
					</li>
					<li>
						<p>
							Framing, embedding, or linking to the Service{' '}
							<b>without authorization</b>.
						</p>
					</li>
					<li>
						<p>
							Interfering with or disrupting the Service, networks, or servers
							connected to the Service, or creating an <b>undue burden</b> on
							our infrastructure.
						</p>
					</li>
					<li>
						<p>
							Decompiling, disassembling, reverse-engineering, or otherwise
							attempting to access the <b>source code</b> of any part of the
							Service.
						</p>
					</li>
					<li>
						<p>
							Bypassing or attempting to bypass <b>access restrictions</b> or
							security measures implemented in the Service.
						</p>
					</li>
					<li>
						<p>
							Uploading, transmitting, or distributing{' '}
							<b>malware, viruses, worms, trojans, or other harmful software</b>{' '}
							that could damage the Service or others’ devices.
						</p>
					</li>
					<li>
						<p>
							Using, launching, or distributing any <b>automated system</b>{' '}
							(e.g., bots, spiders, scrapers, cheat utilities) to access or
							interact with the Service.
						</p>
					</li>
					<li>
						<p>
							Sending <b>unsolicited commercial emails</b> or engaging in
							spam-related activities.
						</p>
					</li>
					<li>
						<p>
							Engaging in any activity that may{' '}
							<b>harm, tarnish, or damage the reputation</b> of the Company or
							the Service.
						</p>
					</li>
					<li>
						<p>
							Using the Service in violation of{' '}
							<b>any applicable laws or regulations</b>.
						</p>
					</li>
					<li>
						<p>
							Create, generating, or distributing content that is intended to
							deceive, mislead, or impersonate any person or entity. This
							includes creating deepfakes, false identification documents, or
							any content designed to misrepresent identity or authenticity.
						</p>
					</li>
				</ul>
				<p>
					<b>Respectful Conduct Towards Customer Support</b>
				</p>
				<p>
					We expect all users to interact with our <b>customer support team</b>{' '}
					in a respectful and professional manner. If at any time your
					communication or behavior is deemed{' '}
					<b>harassing, abusive, threatening, or offensive</b>, we{' '}
					<b>reserve the right to terminate your account immediately</b>.
				</p>

				<p>
					<b>DISCLAIMER OF WARRANTIES</b>
				</p>
				<p>
					<b>General Disclaimers</b>
				</p>
				<p>
					<b>
						EXCEPT TO THE EXTENT PROHIBITED BY LAW OR OTHERWISE INAPPLICABLE,
						YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT YOUR USE OF THE SERVICE IS
						AT YOUR OWN RISK.
					</b>{' '}
					THE SERVICE AND ANY PRODUCTS OR CONTENT PROVIDED THROUGH IT ARE MADE
					AVAILABLE <b>"AS IS" AND "AS AVAILABLE,"</b> WITHOUT ANY WARRANTIES OR
					GUARANTEES OF ANY KIND, EXPRESS OR IMPLIED.
				</p>
				<p>
					TO THE FULLEST EXTENT PERMITTED BY LAW, THE COMPANY AND ITS
					AFFILIATES, OFFICERS, EMPLOYEES, AGENTS, PARTNERS, AND LICENSORS
					EXPRESSLY DISCLAIM ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR
					STATUTORY, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF{' '}
					<b>
						MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT,
						ACCURACY, AND RELIABILITY OF CONTENT OR DATA.
					</b>
				</p>
				<p>IN PARTICULAR, WE DO NOT WARRANT THAT:</p>
				<ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
					<li>
						<p>
							The <b>Service will meet your expectations or requirements</b>;
						</p>
					</li>
					<li>
						<p>
							The{' '}
							<b>
								Service will be uninterrupted, secure, error-free, or free from
								technical issues
							</b>
							;
						</p>
					</li>
					<li>
						<p>
							The{' '}
							<b>
								results obtained from using the Service will be accurate,
								reliable, or error-free
							</b>
							;
						</p>
					</li>
					<li>
						<p>
							The{' '}
							<b>
								quality of any content, features, or services will meet your
								expectations
							</b>
							;
						</p>
					</li>
					<li>
						<p>
							Any <b>defects or errors will be corrected promptly or at all</b>.
						</p>
					</li>
				</ul>
				<p>
					ANY MATERIAL, DATA, OR INFORMATION OBTAINED THROUGH THE SERVICE IS{' '}
					<b>ACCESSED AT YOUR OWN DISCRETION AND RISK</b>. YOU ARE SOLELY
					RESPONSIBLE FOR ANY DAMAGE TO YOUR DEVICE OR LOSS OF DATA RESULTING
					FROM YOUR USE OF THE SERVICE.
				</p>
				<p>
					WE DO NOT GUARANTEE OR PROMISE ANY SPECIFIC RESULTS FROM USING THE
					SERVICE. BY USING THE SERVICE, YOU ACCEPT THE{' '}
					<b>
						INHERENT RISKS OF SERVICE INTERRUPTIONS, TECHNICAL FAILURES, AND
						POTENTIAL DATA LOSS.
					</b>
				</p>
				<p>
					<b>No Professional or Medical Advice</b>
				</p>
				<p>
					ANY INFORMATION OR STATEMENTS AVAILABLE THROUGH THE SERVICE ARE FOR{' '}
					<b>INFORMATIONAL AND GENERAL PURPOSES ONLY</b>. THEY ARE NOT INTENDED
					TO REPLACE OR SUBSTITUTE FOR{' '}
					<b>
						PROFESSIONAL FINANCIAL, MEDICAL, LEGAL, OR OTHER SPECIALIZED ADVICE
					</b>
					.
				</p>
				<p>
					THE COMPANY DOES NOT REPRESENT OR WARRANT THAT ANY INFORMATION
					PROVIDED THROUGH THE SERVICE IS RELIABLE, COMPLETE, OR SUITABLE FOR
					YOUR SPECIFIC NEEDS. YOU ACKNOWLEDGE THAT ANY{' '}
					<b>
						DECISIONS MADE BASED ON INFORMATION PROVIDED THROUGH THE SERVICE ARE
						YOUR SOLE RESPONSIBILITY
					</b>
					, AND IF YOU REQUIRE PROFESSIONAL ADVICE, YOU SHOULD CONSULT A
					QUALIFIED SPECIALIST.
				</p>
				<p>
					TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE COMPANY EXPRESSLY
					DISCLAIMS ANY LIABILITY FOR YOUR RELIANCE ON ANY STATEMENTS,
					RECOMMENDATIONS, OR CONTENT PROVIDED THROUGH THE SERVICE.
				</p>
				<p>
					<b>AI-Generated Content Disclaimer</b>
				</p>
				<p>
					Certain features of our Service utilize artificial intelligence
					technology to generate and modify images. THE COMPANY MAKES NO
					WARRANTIES THAT AI-GENERATED OUTPUTS WILL MEET YOUR EXPECTATIONS, BE
					ERROR-FREE, OR BE SUITABLE FOR YOUR INTENDED PURPOSE.{' '}
					<b>
						THE COMPANY MAKES NO REPRESENTATIONS OR WARRANTIES REGARDING
						INTELLECTUAL PROPERTY RIGHTS IN, OR ACCURACY OF, AI SYSTEM OUTPUTS
					</b>
					. AI systems may produce unexpected, biased, or inaccurate results.
					You are solely responsible for reviewing and validating all
					AI-generated content before use. To the fullest extent permitted by
					law, we disclaim all liability for any damages arising from your use
					of or reliance on AI-generated content.
				</p>
				<p>
					<b>Service Modifications and Updates</b>
				</p>
				<p>
					We reserve the right to{' '}
					<b>
						update, modify, or discontinue any aspect of the Service, including
						features, content, and availability, at any time, with or without
						notice
					</b>
					. This includes changes to:
				</p>
				<ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
					<li>
						<p>
							The information provided on our website and mobile applications;
						</p>
					</li>
					<li>
						<p>The availability or functionality of any Service feature;</p>
					</li>
					<li>
						<p>The pricing, structure, or terms of use of the Service.</p>
					</li>
				</ul>
				<p>
					We are not responsible for any loss or inconvenience resulting from
					modifications, suspensions, or discontinuations of any part of the
					Service.
				</p>
				<p>
					<b>Consumer Protection and Non-Waivable Rights</b>
				</p>
				<p>
					Nothing in these Terms shall exclude or limit any consumer rights that{' '}
					<b>cannot be waived under applicable law</b>. If you are entitled to
					statutory rights under the laws of your country of residence, those
					rights remain unaffected by these disclaimers.
				</p>

				<p>
					<b>LIMITATION OF LIABILITY</b>
				</p>
				<p>
					<b>
						TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE (INCLUDING OUR
						AFFILIATES, OFFICERS, EMPLOYEES, AGENTS, PARTNERS, AND LICENSORS)
						SHALL NOT BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY INDIRECT,
						INCIDENTAL, CONSEQUENTIAL, EXEMPLARY, SPECIAL, OR PUNITIVE DAMAGES,
						INCLUDING BUT NOT LIMITED TO LOST PROFITS, LOST DATA, BUSINESS
						INTERRUPTION, OR ANY OTHER LOSSES ARISING FROM YOUR USE OF OR
						INABILITY TO USE THE SERVICE, PRODUCTS, OR ANY THIRD-PARTY ADS, EVEN
						IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
					</b>
				</p>
				<p>
					YOUR ACCESS TO AND USE OF THE SERVICE (INCLUDING THE APP, WEBSITE,
					CONTENT, AND USER CONTENT) AND THIRD-PARTY ADS ARE AT YOUR OWN RISK.
					YOU AGREE THAT YOU ARE SOLELY RESPONSIBLE FOR ANY DAMAGE TO YOUR
					DEVICE, LOSS OF DATA, OR OTHER HARM THAT RESULTS FROM YOUR USE OF THE
					SERVICE.
				</p>
				<p>
					<b>Limitation of Aggregate Liability</b>
				</p>
				<p>
					NOTWITHSTANDING ANYTHING TO THE CONTRARY HEREIN,{' '}
					<b>
						OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING OUT OF OR RELATED
						TO YOUR USE OF THE SERVICE, PRODUCTS, OR CONTENT SHALL BE LIMITED TO
						THE TOTAL AMOUNT PAID BY YOU TO US FOR ACCESS TO THE SERVICE DURING
						THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE
						TO THE CLAIM, OR IF GREATER, ONE HUNDRED EURO (€ 100).
					</b>
				</p>
				<p>
					THESE LIMITATIONS OF LIABILITY FORM A FUNDAMENTAL BASIS OF THE
					AGREEMENT BETWEEN YOU AND THE COMPANY. WITHOUT THESE LIMITATIONS, WE
					WOULD NOT BE ABLE TO OFFER THE SERVICE UNDER THE SAME TERMS.
				</p>
				<p>
					<b>Waiver of Unknown Claims (California Residents)</b>
				</p>
				<p>
					IF YOU ARE A RESIDENT OF CALIFORNIA, YOU EXPRESSLY WAIVE{' '}
					<b>CALIFORNIA CIVIL CODE SECTION 1542</b>, WHICH STATES:
				</p>
				<p>
					"A GENERAL RELEASE DOES NOT EXTEND TO CLAIMS THAT THE CREDITOR OR
					RELEASING PARTY DOES NOT KNOW OR SUSPECT TO EXIST IN HIS OR HER FAVOR
					AT THE TIME OF EXECUTING THE RELEASE, AND THAT, IF KNOWN BY HIM OR
					HER, WOULD HAVE MATERIALLY AFFECTED HIS OR HER SETTLEMENT WITH THE
					DEBTOR OR RELEASED PARTY."
				</p>
				<p>
					BY ACCEPTING THESE TERMS, YOU RECOGNIZE AND AGREE THAT YOU MAY BE
					WAIVING RIGHTS WITH RESPECT TO CLAIMS THAT ARE CURRENTLY UNKNOWN OR
					UNSUSPECTED.
				</p>
				<p>
					<b>Jurisdiction-Specific Exceptions</b>
				</p>
				<p>
					SOME JURISDICTIONS DO NOT ALLOW CERTAIN LIMITATIONS OR EXCLUSIONS OF
					LIABILITY, INCLUDING FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES. TO THE
					EXTENT THAT{' '}
					<b>
						ANY PART OF THESE LIMITATIONS IS FOUND TO BE UNENFORCEABLE UNDER
						APPLICABLE LAW, THE REMAINING LIMITATIONS SHALL STILL APPLY
					</b>{' '}
					TO THE MAXIMUM EXTENT PERMITTED.
				</p>
				<p>
					IF ANY REMEDY SET FORTH IN THESE TERMS IS FOUND TO HAVE FAILED ITS
					ESSENTIAL PURPOSE, ALL REMAINING LIMITATIONS OF LIABILITY SHALL STILL
					APPLY. ADDITIONAL CONSUMER RIGHTS MAY APPLY DEPENDING ON YOUR
					JURISDICTION.
				</p>

				<p>
					<b>INDEMNIFICATION</b>
				</p>
				<p>
					You agree to <b>defend, indemnify, and hold harmless</b> the Company,
					along with its{' '}
					<b>
						affiliates, parent companies, officers, employees, agents, partners,
						licensors, contractors, successors, and assigns
					</b>{' '}
					(each, an “<b>Indemnitee</b>”), from and against any{' '}
					<b>
						losses, damages, liabilities, claims, demands, judgments,
						settlements, penalties, fines, costs, and expenses
					</b>{' '}
					of any kind—including, but not limited to,{' '}
					<b>reasonable attorneys’ fees and professional fees</b>—arising
					directly or indirectly from:
				</p>
				<ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
					<li>
						<p>
							<b>User Content</b>, including any claims that such content
							infringes upon third-party rights or violates applicable laws.
						</p>
					</li>
					<li>
						<p>
							<b>Your breach of these Terms</b>, whether by you or anyone using
							your account or device.
						</p>
					</li>
					<li>
						<p>
							<b>Your access to or use of the Service</b>, including any actions
							taken on the platform.
						</p>
					</li>
					<li>
						<p>
							<b>
								Your violation of any applicable law, regulation, or third-party
								rights
							</b>
							, including intellectual property, privacy, or proprietary rights.
						</p>
					</li>
					<li>
						<p>
							<b>
								Any claims related to property damage, personal injury, bodily
								harm, or death
							</b>{' '}
							resulting from your use of the Service in violation of these
							Terms.
						</p>
					</li>
				</ul>
				<p>
					The Company reserves the right to <b>assume full control</b> of the
					defense, negotiation, and settlement of any claim for which you are
					required to indemnify us. You agree to <b>fully cooperate</b> with our
					defense efforts and acknowledge that we have the sole discretion to
					select legal counsel and strategy in such matters. You may not settle
					any claim that imposes liability or obligations on the Company without
					our prior written consent.
				</p>

				<p>
					<b>INTERNATIONAL USE</b>
				</p>
				<p>
					The Company makes no representation that the Service is accessible,
					appropriate or legally available for use in your jurisdiction, and
					accessing and using the Service is prohibited from territories where
					doing so would be illegal. You access the Service at your own
					initiative and are responsible for compliance with local laws.
				</p>

				<p>
					<b>INFORMAL DISPUTE RESOLUTION PROCEDURES</b>
				</p>
				<p>
					<b>
						PLEASE READ THIS PROVISION CAREFULLY TO ENSURE THAT YOU
						UNDERSTAND—THIS SECTION CONTROLS HOW DISPUTES BETWEEN YOU AND THE
						COMPANY WILL BE ADDRESSED.
					</b>
				</p>
				<p>
					<b>
						BY AGREEING TO THIS PROVISION, YOU ARE WAIVING YOUR RIGHT TO
						PARTICIPATE IN A CLASS ACTION LAWSUIT AND YOU ARE WAIVING YOUR RIGHT
						TO A JURY TRIAL.
					</b>
				</p>
				<p>
					<b>
						YOU ARE ALSO AGREEING TO RESOLVE ALL DISPUTES BETWEEN YOU AND THE
						COMPANY THROUGH BINDING ARBITRATION UNLESS YOU EXERCISE YOUR RIGHT
						TO REJECT ARBITRATION AS PROVIDED BELOW.
					</b>
				</p>
				<p>
					You and FunplaceApp Limited (“<b>we</b>” or the “<b>Company</b>”)
					agree to resolve all Disputes (including any related disputes
					involving the Company, its subsidiaries, or its affiliates) through
					binding arbitration, as described below, except for: (i) claims that
					fall within the jurisdiction of a small claims court, provided such
					claims are not class action disputes and also meet the court’s
					jurisdictional and monetary limits; and (ii) disputes related to
					intellectual property rights. A “<b>Dispute</b>” means any claim,
					controversy, or legal action—whether arising from past, present, or
					future events, and based on contract, tort, statute, or common
					law—between you and the Company regarding the Website, Services, or
					this agreement (the “<b>Arbitration Agreement</b>”). “Dispute” also
					includes disputes about the interpretation, applicability, or
					enforceability of these terms or the formation of this Arbitration
					Agreement, including whether any part of it is invalid or
					unenforceable.
				</p>
				<p>
					<b>Mandatory Pre-Filing Notice Procedure</b>
				</p>
				<p>
					You and we agree that good faith, informal efforts to resolve disputes
					often result in a faster, inexpensive outcome. Therefore, if you
					intend to assert a claim for any Dispute (as defined above) against
					the Company, you must first send the Company a written notice of the
					Dispute (“<b>Notice</b>”) that gives the Company some basic
					information about you and the Dispute. Any Notice must include (i)
					your name, address, and email address, (ii) a detailed description of
					your Dispute; (iii) any relevant facts regarding your use of the
					Website and Service (including your account ID, profile screenshots,
					and anything else that will help us identify your account; (iv) a
					detailed description of the relief you are seeking, including a
					calculation of any money damages you are seeking; and (v) a personally
					signed statement from you (and not your attorney) verifying the
					accuracy of the information in Notice. The Notice must be
					individualized, meaning it can concern only your dispute and no other
					person’s dispute. If you are filling out a Notice for another person,
					you must include all information described above, and also a statement
					describing your relationship to the person and why the person is
					unable to fill out the Notice for themselves.
				</p>
				<p>You must send the Notice to the Company at the following address:</p>
				<p>8 Ioustinianou Street, Flat 302, 2413 Nicosia, Cyprus</p>
				<p>Attention: Legal</p>
				<p>
					If we need to send you a Notice, we will send the Notice to you at the
					contact information we have available for you, which may include, if
					applicable, the contact information associated with your account.
				</p>
				<p>
					After we receive a Notice, you and us agree to engage in good faith
					efforts to resolve the Dispute between us for a period of 60 days
					through informal negotiation. The 60-day period can be extended if you
					and we agree that such an extension is likely to lead to resolution.
					As part of the informal negotiation process, you and we agree that we
					will both attend at least one individualized video conference (”
					<b>Video Conference</b>”). The Video Conference can be via Zoom,
					Microsoft Teams, WhatsApp, or any other similar platform that you and
					we agree on and that we both have access to. The Video Conference can
					be held after the 60-day period, if necessary. If you are represented
					by an attorney in your Dispute, your attorney may participate in the
					Video Conference, but you are still required to attend and participate
					in good faith. The Company is also required to participate in the
					Video Conference by sending one or more of its representatives, and
					the Company may also send one or more of its attorneys. If you are
					unable to participate in the Video Conference by video, you may attend
					telephonically if you certify in writing that circumstances exist that
					prevent you from appearing by video (such as your lack of access to
					phone with a working camera or your inability to connect to a stable
					internet connection). You and we agree that we (and our attorneys, if
					represented) shall work cooperatively to schedule the Video Conference
					at the earliest mutually-convenient time after we receive a Notice.
					You and we also agree to use our best efforts to resolve the Dispute
					at the Video Conference. If you and we cannot resolve the issues
					identified in the Notice within 60 days after the completed Notice is
					received (or a longer time if agreed), you or we may commence an
					arbitration proceeding or a small claims court proceeding.
				</p>
				<p>
					Compliance with these Informal Dispute Resolution Procedures is
					Mandatory and Pre-Filing Notice procedures (including the Video
					Conference requirement) are a condition precedent to initiating any
					arbitration or small claims court action. Failure to follow the
					procedures is a breach of this Arbitration Agreement.
				</p>
				<p>
					The Mandatory Pre-Filing Notice procedures are essential so that you
					and the Company have a meaningful opportunity to resolve Disputes in
					an inexpensive and efficient manner. Unless prohibited by applicable
					law, the arbitration provider shall not accept or administer any
					demand for arbitration unless the party bringing the demand for
					arbitration certifies in writing that the Mandatory Pre-Filing Notice
					procedures (including the Video Conference requirement) were fully
					satisfied. If the party bringing the demand for arbitration fails to
					include a written certification that the Pre-Filing Notice procedures
					(including the Video Conference) were met, then the arbitration forum
					shall administratively close the demand for arbitration and no fees
					shall be due from the responding party. A court of competent
					jurisdiction shall have authority to enforce this provision and to
					enjoin any arbitration proceeding or small claims court action
					accordingly.
				</p>
				<p>
					All offers, promises, conduct, and statements made in the course of
					the Mandatory Pre-Filing Notice process by any party, its agents,
					employees, and attorneys are confidential and not admissible for any
					purpose in any subsequent proceeding (except as required to certify in
					writing that the Mandatory Pre-Filing Notice procedures were completed
					before submitting a demand for arbitration). Evidence that is
					otherwise admissible or discoverable shall not be rendered
					inadmissible or non-discoverable by this section.
				</p>
				<p>
					<b>Small Claims Court</b>
				</p>
				<p>
					Subject to applicable jurisdictional requirements and Mandatory
					Pre-Filing Notice requirements explained above, you or the Company may
					elect to pursue a Dispute in a local small claims court rather than
					through arbitration, so long as the matter remains in small claims
					court and proceeds only on an individual basis. If a party has already
					submitted an arbitration demand, the other party may, in its sole
					discretion, inform the arbitral forum that it chooses to have the
					Dispute heard in small claims court. At that time, the arbitral forum
					will administratively close the arbitration and the Dispute will be
					heard in the appropriate small claims court, with no fees due from the
					arbitration respondent.
				</p>
				<p>
					<b>What is Arbitration?</b>
				</p>
				<p>
					Arbitration is a more informal way to resolve our disagreements than a
					lawsuit in court. For instance, arbitration uses a neutral arbitrator
					instead of a judge or jury, involves more limited discovery, and is
					subject to very limited review by courts. Although the process is more
					informal, arbitrators can award some of the same individualized
					damages and relief that a court can award. An arbitrator cannot,
					however, order a party to act or stop doing something—this is known as
					“equitable relief.” Either you or we can go to court and seek
					equitable relief, including by filing a motion to compel the other
					party to follow this Arbitration Agreement. However, you and we agree
					that the only courts where we will seek equitable relief are the state
					and federal courts in Delaware. This exception for equitable relief
					does <b>not</b> waive this Arbitration Agreement. You and we agree
					that the U.S. Federal Arbitration Act and federal arbitration law
					govern the interpretation and enforcement of this provision. A court
					of competent jurisdiction has exclusive authority to resolve any
					dispute relating to the interpretation, applicability, or
					enforceability of this binding arbitration agreement. This arbitration
					provision shall survive termination of these terms and the termination
					of your account.
				</p>
				<p>
					<b>CLASS ACTION AND JURY TRIAL WAIVER</b>
				</p>
				<p>
					<b>
						TO THE FULLEST EXTENT ALLOWABLE BY LAW, YOU AND THE COMPANY WAIVE
						THE RIGHT TO A JURY TRIAL AND THE RIGHT TO LITIGATE DISPUTES IN
						COURT IN FAVOR OF ARBITRATION (EXCEPT FOR SMALL CLAIMS COURT
						DESCRIBED ABOVE). YOU AND THE COMPANY EACH WAIVE THE RIGHT TO FILE
						OR PARTICIPATE IN A CLASS ACTION LAWSUIT AGAINST THE OTHER,
						INCLUDING ANY CURRENTLY PENDING ACTIONS AGAINST THE COMPANY. TO THE
						FULLEST EXTENT ALLOWABLE BY LAW, THERE SHALL BE NO RIGHT OR
						AUTHORITY FOR ANY CLAIMS TO BE LITIGATED IN COURT ON A CLASS,
						COLLECTIVE, REPRESENTATIVE, OR CONSOLIDATED BASIS.
					</b>
				</p>
				<p>
					<b>
						EXCEPT FOR THE MASS FILING PROCEDURES DESCRIBED BELOW, YOU AND WE
						AGREE THAT
					</b>
				</p>
				<ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
					<li>
						<p>
							<b>
								THE ARBITRATOR MAY ONLY AWARD FINAL RELIEF IN FAVOR OF THE
								INDIVIDUAL PARTY SEEKING RELIEF AND ONLY TO THE EXTENT NECESSARY
								TO PROVIDE FINAL RELIEF WARRANTED BY THAT INDIVIDUAL PARTY’S
								CLAIM.
							</b>
						</p>
					</li>

					<li>
						<p>
							<b>
								THE ARBITRATOR MAY NOT AWARD FINAL RELIEF FOR, AGAINST, OR ON
								BEHALF OF ANYONE WHO IS NOT A PARTY TO THE ARBITRATION ON A
								CLASS, COLLECTIVE, OR REPRESENTATIVE BASIS.
							</b>
						</p>
					</li>
				</ul>

				<p>
					<b>
						IF A COURT DETERMINES THAT ANY OF THE PROHIBITIONS IN THIS PARAGRAPH
						ARE UNENFORCEABLE FOR A PARTICULAR CLAIM OR REQUEST FOR RELIEF, AND
						ALL APPEALS OF THAT DECISION ARE AFFIRMED AND SUCH DECISION BECOMES
						FINAL, THEN YOU AND THE COMPANY AGREE THAT THAT PARTICULAR CLAIM OR
						REQUEST FOR RELIEF SHALL PROCEED IN COURT BUT SHALL BE STAYED
						PENDING INDIVIDUAL ARBITRATION OF THE REMAINING CLAIMS FOR RELIEF
						THAT YOU HAVE BROUGHT. IF THIS SPECIFIC PARAGRAPH IS FOUND TO BE
						UNENFORCEABLE, THEN THE ENTIRETY OF THIS ARBITRATION PROVISION
						(EXCEPT FOR THE JURY TRIAL WAIVER AND THE INFORMAL DISPUTE
						RESOLUTION PROCEDURE) SHALL BE NULL AND VOID.
					</b>
				</p>
				<p>
					<b>Arbitration Procedure</b>
				</p>
				<p>
					The arbitration will be governed by applicable rules of National
					Arbitration & Mediation (“<b>NAM</b>”) (including the Comprehensive
					Dispute Resolution Rules and Procedures and the Supplemental Rules for
					Mass Arbitration Filings, as applicable) (“<b>NAM Rules</b>”), as
					modified by this Arbitration Agreement, and will be administered by
					NAM. The NAM Rules are available online at www.namadr.com or by
					requesting them in writing at the Notice address listed above. You may
					obtain a form to initiate arbitration with NAM at:
					https://www.namadr.com/content/uploads/2024/03/Comprehensive-Demand-for-Arb-revised-3.21.2024.pdf
					or by contacting NAM.
				</p>
				<p>
					If NAM is unavailable or unwilling to do so, another arbitration
					provider shall be selected by the parties that will do so, or if the
					parties are unable to agree on an alternative administrator, by the
					court pursuant to 9 U.S.C. §5.
				</p>
				<p>
					<b>
						You and we agree that the party initiating arbitration must submit a
						written certification that they have complied with and completed the
						Mandatory Pre-Filing Notice and Informal Dispute Resolution
						Procedures requirements enclosed with any demand for arbitration.
						The demand for arbitration and certification must be personally
						signed by the party initiating arbitration (and their attorney, if
						represented).
					</b>
				</p>
				<p>
					The arbitration will be in English. A single independent and impartial
					arbitrator will be appointed remotely pursuant to the NAM Rules, as
					modified herein. You and the Company agree to comply with the
					following rules, which are intended to streamline the dispute
					resolution process and reduce the costs and burdens on the parties:
					(i) the arbitration will be conducted online and/or be solely based on
					written submissions, the specific manner to be chosen by the party
					initiating the arbitration; (ii) the arbitration will not require any
					personal appearance by the parties or witnesses unless otherwise
					mutually agreed in writing by the parties or the arbitrator decides
					that a formal hearing is necessary.; and (iii) any judgment on the
					award the arbitrator renders may be entered in any court of competent
					jurisdiction.
				</p>
				<p>
					If an in-person hearing is required and you reside in the United
					States, the hearing will take place in either Delaware, unless the
					arbitrator determines that this would pose a hardship for you, in
					which case the in-person hearing may be conducted in the claimant’s
					state and county of residence. If you reside outside the United
					States, the site of any in-person hearing will be determined by the
					NAM Rules.
				</p>
				<p>
					The award of the arbitrator will be in writing and will include a
					statement setting forth the reasons for the disposition of any claim.
					The arbitrator will apply the laws of the State of Florida in
					conducting the arbitration. You acknowledge that these terms and your
					use of the Service evidences a transaction involving interstate
					commerce. The United States Federal Arbitration Act will govern the
					interpretation, enforcement, and proceedings.
				</p>
				<p>
					The Arbitrator is bound by and shall adhere to this Arbitration
					Agreement. In the event NAM Rules conflict with this Arbitration
					Agreement, the terms of this Arbitration Agreement shall control. If
					the Arbitrator determines that strict application of any term of this
					Arbitration Agreement would result in a fundamentally unfair
					arbitration, then the Arbitrator shall have the authority to modify
					such term to the extent necessary to ensure a fundamentally fair
					arbitration that is consistent with efficient and inexpensive
					resolution of Disputes.
				</p>
				<p>
					Unless you and Сompany otherwise agree, the arbitration will be
					conducted virtually via video or teleconference.
				</p>
				<p>
					<b>Decision of the Arbitrator</b>
				</p>
				<p>
					Barring extraordinary circumstances, the arbitrator will issue their
					decision within 120 days from the date the arbitrator is appointed.
					The arbitrator may extend this time limit for an additional 30 days in
					the interests of justice. All arbitration proceedings will be closed
					to the public and confidential, and all records relating thereto will
					be permanently sealed, except as necessary to obtain court
					confirmation of the arbitration award. The award of the arbitrator
					will be in writing and will include a statement setting forth the
					reasons for the disposition of any claim.
				</p>
				<p>
					The arbitration award is binding only between you and the Company and
					will not have any preclusive effect in another arbitration or
					proceeding that involves a different party.
				</p>
				<p>
					<b>Fees</b>
				</p>
				<p>
					The payment of arbitration fees (the fees imposed by the arbitration
					administrator including filing, arbitrator, and hearing fees) will be
					governed by the applicable NAM Rules, unless you qualify for a fee
					waiver under applicable law. If after exhausting any potentially
					available fee waivers, the arbitrator finds that the arbitration fees
					will be prohibitive for you as compared to litigation, we will pay as
					much of your filing, arbitrator, and hearing fees in the arbitration
					as the arbitrator deems necessary to prevent the arbitration from
					being cost-prohibitive, regardless of the outcome of the arbitration,
					unless the arbitrator determines that your claim(s) were frivolous or
					brought for an improper purpose or asserted in bad faith.
				</p>
				<p>
					You and we agree that arbitration should be cost-effective for all
					parties and that any party may engage with NAM to address the
					reduction or deferral of fees.
				</p>
				<p>
					<b>Confidentiality</b>
				</p>
				<p>
					Upon either your or our request, the Arbitrator will issue an order
					requiring that confidential information of either party disclosed
					during the arbitration (whether in documents or orally) may not be
					used or disclosed except in connection with the arbitration or a
					proceeding to enforce the arbitration award and that any permitted
					court filing of confidential information must be done under seal.
				</p>
				<p>
					<b>Settlement Offers and Offers of Judgment</b>
				</p>
				<p>
					At least ten (10) calendar days before the date set for the
					arbitration hearing, you or the Company may serve a written offer of
					judgment upon the other party to allow judgment on specified terms. If
					the offer is accepted, the offer with proof of acceptance shall be
					submitted to the arbitration provider, who shall enter judgment
					accordingly. If the offer is not accepted prior to the arbitration
					hearing or within thirty (30) calendar days after it is made,
					whichever occurs first, it shall be deemed withdrawn, and cannot be
					given as evidence in the arbitration. If an offer made by one party is
					not accepted by the other party, and the other party fails to obtain a
					more favorable award, the other party shall not recover their
					post-offer costs and shall pay the offering party’s costs from the
					time of the offer (which, solely for purposes of offers of judgment,
					may include reasonable attorneys’ fees to the extent they are
					recoverable by statute, in an amount not to exceed the damages
					awarded).
				</p>
				<p>
					The parties agree that any disputes with respect to settlement
					offer(s) or offer(s) of judgment in a Mass Filing are to be resolved
					by a single arbitrator to the extent such offers contain the same
					material terms. For arbitrations involving represented parties, the
					represented parties’ attorneys agree to communicate individual
					settlement offer(s) or offer(s) of judgment to each and every
					arbitration claimant or respondent to whom such offers are extended.
				</p>
				<p>
					<b>Additional Procedures for Mass Arbitration Filings</b>
				</p>
				<p>
					The following provisions set forth additional procedures that apply to
					mass arbitration filings. If ten (10) or more similar claims are
					asserted against the Company by the same or coordinated attorneys or
					are otherwise coordinated, consistent with the definition and criteria
					of “<b>Mass Filings</b>” set forth in the NAM Rules, you and we
					understand and agree that these additional procedures shall apply and
					the resolution of your dispute might be delayed. You and we agree that
					throughout this process, our attorneys shall meet and confer to
					discuss modifications to these procedures based on the particular
					needs of the Mass Filing. You and we agree to make all reasonable
					efforts to maximize the integrity and efficiency of arbitration to
					resolve Disputes between us, particularly those involving Mass
					Filings, and further commit to acting in good faith to adhere to the
					procedures established in this section. The parties further agree that
					application of these Mass Filing procedures have been reasonably
					designed to result in an efficient and fair adjudication of claims.
				</p>
				<p>
					<b>Bellwether Arbitrations for Mass Filings.</b> Bellwether
					proceedings are encouraged by courts and arbitration administrators
					where there are multiple disputes involving similar claims against the
					same or related parties. The parties shall select ten individual
					arbitration claims (five per side), designated as the “Initial Test
					Cases,” to proceed to arbitration. Only the Initial Test Cases shall
					be filed with the arbitrator. All other claims shall be held in
					abeyance. This means that the filing fees will be paid only for the
					Initial Test Cases; for all other demands for arbitration in a Mass
					Filing, the filing fees (together with any arbitrator consideration of
					the other demands) will be held in abeyance, and neither you nor the
					Company will be required to pay any such filing fees. You and the
					Company also agree that neither you nor we shall be deemed to be in
					breach of this Arbitration Agreement for failure to pay any such
					filing fees, and that neither you nor we shall be entitled to any
					contractual, statutory, or other remedies, damages, or sanctions of
					any kind for failure to pay any such filing fees. If, pursuant to this
					subsection, a party files non-Bellwether Arbitrations with the
					arbitration provider, the parties agree that the arbitration provider
					shall hold those demands in abeyance and not refer them to the
					arbitrator pending resolution of the Initial Test Cases. Unless the
					claims are resolved in advance or the schedule is extended, the
					arbitrators will render a final award for the Initial Test Cases
					within 120 days of the initial pre-hearing conference.
				</p>
				<p>
					<b>Global Mediation in Mass Filings.</b> Following the resolution of
					the Initial Test Cases, the parties agree to engage in a global
					mediation of all the remaining individual arbitration claims
					comprising the Mass Filing (“<b>Global Mediation</b>”), deferring any
					filing costs associated with the non-Initial Test Cases until the
					Initial Test Cases and subsequent Global Mediation have concluded.
					After the final awards are provided to the mediator in the Initial
					Test Cases, the mediator and the parties shall have 90 days to agree
					upon a substantive methodology and make an offer to resolve the
					outstanding cases. If the Parties are unable to resolve the
					outstanding claims during the Global Mediation, the Parties may choose
					to opt out of the arbitration process and proceed in court with the
					remaining claims. Notice of the opt-out shall be provided in writing
					within 60 days of the close of the Global Mediation. Absent notice of
					an opt-out, the arbitrations may then be filed and administered by the
					arbitration provider. You and we also acknowledge that any applicable
					statute of limitations shall be tolled pending resolution of the
					global mediation process.
				</p>
				<p>
					<b>Severability.</b> If any part of this Mass Arbitration provision is
					declared invalid, void, or unenforceable, then that provision is
					severable from the Arbitration Agreement and shall not affect the
					validity and enforceability of the remaining provisions.
				</p>
				<p>
					<b>Opting Out of this Arbitration Agreement.</b>
				</p>
				<p>
					<b>Existing Users.</b> Users who previously agreed to arbitrate may
					reject this updated Arbitration Agreement by following the opt-out
					method below, but such users will still be bound by the most recent
					prior version of the Arbitration Agreement and will otherwise be bound
					by these terms. Previous or existing users who do not opt out of this
					updated Arbitration Agreement will be bound by this Arbitration
					Agreement and it shall apply to all disputes between such users and
					the Company, including those arising (but not actually filed in
					arbitration) before the effective date of these terms. Arbitration
					demands that have already been actually filed with an arbitration
					provider before the effective date of this Arbitration Agreement and
					in compliance with a prior version of this Arbitration Agreement are
					subject to the prior version's terms.
				</p>
				<p>
					<b>New Users.</b> Users who create an account with the Company for the
					first time on or after <b>May 7, 2026</b> may opt out of this
					Arbitration Agreement.
				</p>
				<p>
					<b>Method and Impact of Opting Out.</b> Subject to the above, you may
					opt out of this Arbitration Agreement by sending written notice of
					your decision to opt out to:{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>
					, (1) within 31 days after the Arbitration Agreement became effective,
					as indicated in the “Last Updated” date of the terms, (2) you first
					use of the Services. Your notice must include:
				</p>
				<ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
					<li>
						<p>Your name</p>
					</li>
					<li>
						<p>Your username (if any)</p>
					</li>
					<li>
						<p>
							The email address and/or phone number you used to set up your
							account (if you have one)
						</p>
					</li>
					<li>
						<p>
							An unequivocal statement that you want to opt out of this
							Arbitration Agreement
						</p>
					</li>
				</ul>
				<p>
					If you opt out of this Arbitration Agreement, all other parts of the
					terms and any other agreements between you and the Company will
					continue to apply to you. Opting out of this Arbitration Agreement has
					no effect on any other arbitration agreements that you may currently
					have, or may enter in the future, with us.
				</p>
				<p>
					<b>Arbitration Agreement Survival.</b> This Arbitration Agreement will
					survive the termination of your relationship with the Company,
					including any revocation of consent or other action by you to end your
					participation in the Service or any communication with the Company.
				</p>
				<p>
					<b>Severability:</b> If any portion of this Arbitration Agreement is
					found to be void, invalid, or otherwise unenforceable, then that
					portion shall be deemed to be severable and, if possible, superseded
					by a valid, enforceable provision, or portion thereof, that matches
					the intent of the original provision, or portion thereof, as closely
					as possible. The remainder of this Arbitration Agreement shall
					continue to be enforceable and valid according to the terms contained
					herein.
				</p>
				<p>
					<b>Governing Law</b>
				</p>
				<p>
					The laws of the <b>Republic of Cyprus</b>, excluding its body of law
					governing conflicts of law principles, govern this Agreement and your
					use of the Service.
				</p>
				<p>
					To the extent that any action relating to any dispute hereunder is
					permitted to be brought in a court of law, such action will be subject
					to the exclusive jurisdiction of the courts of Cyprus. You hereby
					irrevocably submit to personal jurisdiction and venue in such courts
					and waive any defense of improper venue or inconvenient forum.
				</p>
				<p>
					<b>EEA or UK residents</b>
				</p>
				<p>
					Nothing in these Terms shall deprive you of the{' '}
					<b>consumer protection rights</b> granted by the <b>mandatory laws</b>{' '}
					of your country of residence.
				</p>
				<p>
					If you have a complaint, please contact us at{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>
					. If we do not resolve your complaint to your satisfaction, you may
					(but are not obligated to) use the{' '}
					<b>Online Dispute Resolution (ODR) platform</b>, available at
					http://ec.europa.eu/odr. The Company does not participate in{' '}
					<b>any alternative dispute resolution scheme</b>, except as required
					by law.
				</p>
				<p>
					If a dispute arises under these Terms, you may{' '}
					<b>
						bring legal proceedings before the competent courts of your habitual
						residence
					</b>{' '}
					in the <b>EEA or UK</b>, and these courts shall have{' '}
					<b>exclusive jurisdiction</b> over the dispute. The Company shall also
					submit any disputes to the courts in your country of habitual
					residence.
				</p>
				<p>
					<b>СALIFORNIA RESIDENT</b>
				</p>
				<p>
					If you are a California resident, in accordance with Cal. Civ. Code §
					1789.3, you may report complaints to the Complaint Assistance Unit of
					the Division of Consumer Services of the California Department of
					Consumer Affairs by contacting them in writing at 1625 North Market
					Blvd., Suite N 112 Sacramento, CA 95834, or by telephone at (800)
					952-5210.
				</p>
				<p>
					<b>LIMITATION ON CLAIMS PERIOD</b>
				</p>
				<p>
					You agree that, regardless of any statute or law to the contrary or
					any applicable dispute resolution process, any{' '}
					<b>claim or cause of action</b> arising from or related to the use of
					the <b>Service</b> or these <b>Terms</b> must be{' '}
					<b>filed within one (1) year</b> from the date the claim or cause of
					action first arose. Failure to do so will result in your claim being{' '}
					<b>permanently barred</b>.
				</p>
				<p>
					The provisions of this section, titled{' '}
					<b>“Limitation on Claims Period”</b>, constitute a{' '}
					<b>separate legally binding agreement</b> between you and the Company.
				</p>

				<p>
					<b>MISCELLANEOUS PROVISIONS</b>
				</p>
				<p>
					No failure or delay by the Company in exercising any of its rights
					under these Terms shall be deemed a <b>waiver of such rights</b>, nor
					shall any partial exercise of rights prevent the further enforcement
					of those or any other rights under these Terms. A waiver of any
					provision shall not constitute a{' '}
					<b>waiver of any subsequent breach or default</b>.
				</p>
				<p>
					If any provision of these Terms is found to be{' '}
					<b>invalid, illegal, or unenforceable</b>, the remainder of these
					Terms shall remain in full force and effect. The invalid or
					unenforceable provision shall be <b>modified or replaced</b> to the
					extent necessary to make it valid and enforceable while maintaining
					the intent of the parties to the fullest extent permitted by law.
				</p>
				<p>
					These Terms constitute the <b>entire agreement</b> between you and the
					Company regarding the subject matter herein and{' '}
					<b>
						supersede all prior agreements, understandings, and representations
					</b>
					, whether written or oral. No modifications or amendments to these
					Terms shall be binding unless made in writing and agreed upon by both
					parties.
				</p>
				<p>
					The Company may <b>assign or transfer</b> its rights and obligations
					under these Terms to any other entity, including through{' '}
					<b>merger, acquisition, corporate restructuring, or novation</b>. By
					continuing to use the Service, you consent to any such transfer or
					assignment, and a notice posted on the Service indicating the change
					shall constitute valid notification.
				</p>
				<p>
					All communications between you and the Company, including notices,
					disclosures, and agreements, shall be conducted electronically. You
					acknowledge that electronic communications, including{' '}
					<b>emails, platform notifications, and digital agreements</b>, hold
					the same legal weight as written documents and constitute a{' '}
					<b>legally binding contract</b>. By clicking buttons labelled{' '}
					<b>"SUBMIT," "CONTINUE," "REGISTER," or "I AGREE"</b>, you affirm your
					intent to be legally bound by these Terms and acknowledge that your
					electronic submission constitutes a <b>valid electronic signature</b>.
				</p>
				<p>
					The Company utilizes <b>third-party providers</b> to facilitate
					various operational and technical functions, including but not limited
					to{' '}
					<b>
						payment processing, customer support, security enhancements, and
						data management
					</b>
					. By using the Service, you acknowledge and agree that these
					third-party service providers may assist in delivering the Service and
					enhancing its functionality.
				</p>
				<p>
					The Company shall not be liable for any failure or delay in complying
					with these Terms where such failure arises from{' '}
					<b>circumstances beyond its reasonable control</b>, including but not
					limited to{' '}
					<b>
						force majeure events, legal or regulatory changes, cyberattacks, or
						unforeseen operational disruptions
					</b>
					.
				</p>
				<p>
					<b>Contact Information</b>
				</p>
				<p>For general inquiries or support, you may contact us at:</p>
				<p>
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>
				</p>
				<p>
					By continuing to use the Service, you{' '}
					<b>
						acknowledge that you have read, understood, and agreed to these
						Terms in their entirety
					</b>
					.
				</p>
			</div>
		</section>
	);
};

export default TermsOfUse;
